/**
 * `PublicCvJobDetail` — variante PUBBLICA (non autenticata) del dettaglio del
 * posizionamento su una professione (Job e-CF 3.0) per un profilo CV esterno.
 *
 * È il pendant di `ExternalCvJobDetail` (area riservata) adattato al contesto
 * pubblico:
 *  - client token-less (`usePublicApiClient`): nessun Bearer, nessun OIDC;
 *  - il profilo è identificato dal Public_Token di rotta
 *    (`/public/submission/:token/jobs/:jobId`), non da un id interno;
 *  - a differenza del flusso interno (che ha un endpoint dedicato
 *    `job-positioning/:jobId`), qui il `JobMatch` completo è già incluso nella
 *    dashboard pubblica (`GET /api/public/public-cv/profiles/:token/dashboard`
 *    → `jobPositioning.jobMatches`): si risolve la professione per `jobId` da
 *    lì, senza un endpoint aggiuntivo.
 *
 * La resa è delegata al componente presentazionale `JobPositioningView` (lo
 * stesso usato dal flusso interno). Il "torna indietro" riporta al profilo
 * pubblico (`/public/submission/:token`) e i link alle competenze puntano al
 * dettaglio competenza pubblico (`/public/submission/:token/competences/:id`).
 */
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { JobMatch, PublicExternalCvDashboardDto } from '../../shared/dto';
import { usePublicApiClient } from '../../api/usePublicApiClient';
import { ApiRequestError } from '../../api/client';
import { JobPositioningView } from '../../components/JobPositioningView';
import { Button, EmptyState, Loading } from '../../components/ui';

export default function PublicCvJobDetail() {
  const api = usePublicApiClient();
  const navigate = useNavigate();
  const { token, jobId } = useParams<{ token: string; jobId: string }>();
  const hasToken = token !== undefined && token !== '';
  const encodedToken = encodeURIComponent(token ?? '');

  // Dashboard pubblica del profilo: include il posizionamento completo sulle
  // professioni. Un profilo inesistente (404) non va ritentato.
  const dashboardQuery = useQuery<PublicExternalCvDashboardDto>({
    queryKey: ['publicCv', 'profiles', 'dashboard', token],
    queryFn: () =>
      api.get<PublicExternalCvDashboardDto>(
        `/api/public/public-cv/profiles/${encodedToken}/dashboard`,
      ),
    enabled: hasToken,
    retry: false,
  });

  const job = useMemo<JobMatch | null>(
    () =>
      (dashboardQuery.data?.jobPositioning.jobMatches ?? []).find(
        (j) => j.jobId === jobId,
      ) ?? null,
    [dashboardQuery.data, jobId],
  );

  const backToProfile = () => {
    void navigate(`/public/submission/${encodedToken}`);
  };

  const backLabel = '← Torna al profilo';

  const competenceDetailPath = (competenceId: string) =>
    `/public/submission/${encodedToken}/competences/${encodeURIComponent(competenceId)}`;

  // ----- Rendering -----

  if (dashboardQuery.isLoading) {
    return (
      <section aria-labelledby="public-job-detail-title">
        <h1 id="public-job-detail-title">Dettaglio professione</h1>
        <Loading>Caricamento della professione…</Loading>
      </section>
    );
  }

  // La dashboard pubblica è la fonte autorevole per l'esistenza del profilo: un
  // `404` (link non valido o scaduto) è gestito con un messaggio dedicato.
  if (dashboardQuery.isError || dashboardQuery.data === undefined || job === null) {
    const err = dashboardQuery.error;
    const notFound = err instanceof ApiRequestError && err.kind === 'not_found';
    const message = notFound
      ? 'Il profilo richiesto non esiste o il link non è più valido.'
      : dashboardQuery.data !== undefined && job === null
        ? 'La professione richiesta non esiste o non è più disponibile.'
        : err instanceof Error
          ? err.message
          : 'La professione richiesta non esiste o non è più disponibile.';
    return (
      <section aria-labelledby="public-job-detail-title">
        <h1 id="public-job-detail-title">Dettaglio professione</h1>
        <EmptyState role="status">{message}</EmptyState>
        <div className="ui-toolbar" style={{ marginTop: 'var(--space-3)' }}>
          <Button type="button" onClick={backToProfile}>
            {backLabel}
          </Button>
        </div>
      </section>
    );
  }

  return (
    <JobPositioningView
      job={job}
      backTo={{ label: backLabel, onClick: backToProfile }}
      competenceDetailPath={competenceDetailPath}
      subtitle="Le competenze richieste dalla professione e il posizionamento del candidato."
    />
  );
}
